// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The selection bar's modify commands as tools on the plan (frontend-u0o.4;
 * design.md §4, Revit's MV CO RO MM OF SL JN TR AL and SketchUp's
 * measurement box): which picks each needs, what the status line says at
 * each step, which value the measurement box takes, and — for the pointer
 * where it is now, or a typed value — the batch it would commit (the same
 * ops draw the preview and are sent on the click). One commit, one undo.
 *
 *  - Flyt / Kopiér: base point → target (typed: Afstand along the drag).
 *  - Rotér: pivot (Enter: the selection's centre) → start direction →
 *    end direction (typed: Vinkel, counter-clockwise, once the pivot is set).
 *  - Spejl: two points on the mirror axis. Kopi on Rotér / Spejl keeps the
 *    originals.
 *  - Forskyd: the pointer's side and distance from the selected wall
 *    (typed: Afstand) → a copy there.
 *  - Del: a click on a wall cuts it there; stays on for the next cut.
 *  - Saml hjørne: two selected walls join at once; one selected waits for
 *    the click on the other.
 *  - Forlæng / trim: the selected wall is the boundary; each wall clicked
 *    goes to it (the clicked part stays).
 *  - Juster: the selected wall goes onto the reference line clicked (a
 *    wall's axis or a plane trace).
 *  - Sammenføj: two selected collinear walls merge at once.
 *
 * Pure: no React, no DOM.
 */

import type { GeometriModel } from './geometri';
import { findElement } from './geometri';
import { mirrorSelection, moveSelection, offsetWall, rotateSelection, alignWall, extendTo } from './modifyAdapter';
import type { XY } from './planWalls';
import { frame } from './wallGrips';
import { joinOps, mergeOps, selectionCentre, splitOps, type OpsResult } from './wallModify';
import { parseLength, elementTitle } from './geometriEdit';

export type ModifyCommand = 'move' | 'copy' | 'rotate' | 'mirror' | 'offset' | 'split' | 'join' | 'merge' | 'trim' | 'align';

/** A line picked on the plan: a wall's axis or a plane trace. */
export interface PickedLine {
  a: XY;
  b: XY;
  source: 'wall' | 'trace';
  guid?: string;
  label: string;
}

export interface ModifySession {
  command: ModifyCommand;
  /** The selection the command works on. */
  guids: readonly string[];
  /** Points clicked so far (base, pivot, reference, axis…). */
  points: XY[];
  /** Kopi (Rotér / Spejl): keep the originals. */
  copy: boolean;
}

/** What lies under the pointer, as the layer resolves it. */
export interface PointerPick {
  /** Where it lands (after inference). */
  p: XY;
  /** The wall it rests on, if any. */
  wall: string | null;
  /** The line it holds to — a wall's axis or a plane trace. */
  line: PickedLine | null;
}

/** Starting a command: a session to run, a batch at once, or why not. */
export type StartResult = { session: ModifySession } | { ops: OpsResult; label: string };

const walls = (model: GeometriModel, guids: readonly string[]) => guids.filter((g) => findElement(model, g)?.kind === 'wall');

export const COMMAND_LABEL: Record<ModifyCommand, string> = {
  move: 'Flyt',
  copy: 'Kopiér',
  rotate: 'Rotér',
  mirror: 'Spejl',
  offset: 'Forskyd',
  split: 'Del',
  join: 'Saml hjørne',
  merge: 'Sammenføj',
  trim: 'Forlæng / trim',
  align: 'Juster',
};

/** Begin `command` on `selection`. */
export function startModify(model: GeometriModel, command: ModifyCommand, selection: readonly string[]): StartResult {
  const ws = walls(model, selection);
  const err = (error: string): StartResult => ({ ops: { error }, label: COMMAND_LABEL[command] });
  const session = (copy = false): StartResult => ({ session: { command, guids: [...selection], points: [], copy } });
  switch (command) {
    case 'merge':
      if (ws.length !== 2) return err('Vælg to vægge på linje for at sammenføje dem');
      return { ops: mergeOps(model, ws[0], ws[1]), label: label(model, 'Sammenføj', ws) };
    case 'join':
      if (ws.length === 2) return { ops: joinOps(model, ws[0], ws[1]), label: label(model, 'Saml hjørne', ws) };
      if (ws.length !== 1) return err('Vælg én eller to vægge for at samle et hjørne');
      return session();
    case 'offset':
    case 'trim':
    case 'align':
      if (ws.length !== 1) return err(`Vælg én væg for at ${command === 'offset' ? 'forskyde den' : command === 'trim' ? 'bruge den som grænse' : 'justere den'}`);
      return session();
    case 'split':
      return session();
    case 'move':
    case 'copy':
    case 'rotate':
    case 'mirror':
      if (selection.length === 0) return err('Vælg noget først');
      return session(command === 'mirror');
  }
}

function label(model: GeometriModel, action: string, guids: readonly string[]): string {
  if (guids.length === 1) {
    const f = findElement(model, guids[0]);
    return f ? `${action} · ${elementTitle(f)}` : action;
  }
  if (guids.length === 2) {
    const a = findElement(model, guids[0]);
    const b = findElement(model, guids[1]);
    if (a && b) return `${action} · ${elementTitle(a)} og ${elementTitle(b)}`;
  }
  return `${action} ${guids.length} elementer`;
}

/** The measurement box's field at this step, or null when nothing is typed here. */
export function typedField(s: ModifySession): string | null {
  switch (s.command) {
    case 'move':
    case 'copy':
      return s.points.length === 1 ? 'Afstand' : null;
    case 'rotate':
      return s.points.length >= 1 ? 'Vinkel' : null;
    case 'offset':
      return 'Afstand';
    default:
      return null;
  }
}

/** The status line at this step: what to click, and the keys that help. */
export function modifyHint(s: ModifySession): { lead: string; keys: { key: string; text: string }[] } {
  const esc = { key: 'Esc', text: s.points.length > 0 ? 'et skridt tilbage' : 'afslut' };
  const typed = (text: string) => ({ key: '0–9', text });
  const name = COMMAND_LABEL[s.command];
  switch (s.command) {
    case 'move':
    case 'copy':
      return s.points.length === 0
        ? { lead: `${name} · klik basispunkt`, keys: [esc] }
        : { lead: `${name} · klik nyt punkt`, keys: [typed('skriv afstand'), { key: '⇧', text: 'lås retning' }, { key: 'Alt', text: 'uden snap' }, esc] };
    case 'rotate':
      if (s.points.length === 0) return { lead: 'Rotér · klik drejepunkt', keys: [{ key: '↵', text: 'midten af det valgte' }, esc] };
      if (s.points.length === 1) return { lead: 'Rotér · klik startretning', keys: [typed('skriv vinkel'), esc] };
      return { lead: 'Rotér · klik slutretning', keys: [typed('skriv vinkel'), { key: '⇧', text: 'lås retning' }, esc] };
    case 'mirror':
      return { lead: s.points.length === 0 ? 'Spejl · klik første punkt på spejlaksen' : 'Spejl · klik andet punkt på spejlaksen', keys: [esc] };
    case 'offset':
      return { lead: 'Forskyd · peg på siden og klik', keys: [typed('skriv afstand'), esc] };
    case 'split':
      return { lead: 'Del · klik på væggen, hvor den skal deles', keys: [{ key: 'Esc', text: 'afslut' }] };
    case 'join':
      return { lead: 'Saml hjørne · klik den anden væg', keys: [esc] };
    case 'trim':
      return { lead: 'Forlæng / trim · klik den del af en væg, der skal blive', keys: [{ key: 'Esc', text: 'afslut' }] };
    case 'align':
      return { lead: 'Juster · klik referencelinjen (væg eller planspor)', keys: [esc] };
    case 'merge':
      return { lead: 'Sammenføj', keys: [esc] };
  }
}

/** A typed angle in degrees ("90", "-45,5"); null for anything else. */
export function parseAngle(text: string): number | null {
  const t = text.trim().replace(',', '.').replace(/°$/, '');
  if (!/^-?\d+(\.\d*)?$|^-?\.\d+$/.test(t)) return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const ang = (from: XY, to: XY) => Math.atan2(to[1] - from[1], to[0] - from[0]);

/** What the session does for the pointer `at` (or a typed value) — the preview's and the click's batch. */
export interface ModifyPreview {
  result: OpsResult;
  label: string;
  /** Lines to draw: the drag from the base, the rotation's rays, the mirror axis. */
  guides: [XY, XY][];
  /** The live value, as the measurement box shows it ("1,20", "90,0"). */
  value: string | null;
}

const fmtLen = (v: number) => v.toFixed(2).replace('.', ',');
const fmtDeg = (v: number) => v.toFixed(1).replace('.', ',');

/**
 * The batch for the pointer at `at` (a click there commits it) and the
 * guides to draw, or null when this step only picks a point (no batch
 * yet). `typed` is the measurement box's text.
 */
export function modifyPreview(model: GeometriModel, s: ModifySession, at: PointerPick | null, typed: string | null, newGuid: () => string): ModifyPreview | null {
  const L = (action: string) => label(model, action, s.guids);
  const typedLen = typed ? parseLength(typed) : null;
  switch (s.command) {
    case 'move':
    case 'copy': {
      if (s.points.length !== 1 || !at) return null;
      const base = s.points[0];
      let delta = sub(at.p, base);
      const d = Math.hypot(delta[0], delta[1]);
      if (typedLen !== null) {
        if (!(d > 1e-9)) return null;
        delta = [(delta[0] / d) * typedLen, (delta[1] / d) * typedLen];
      }
      const to: XY = [base[0] + delta[0], base[1] + delta[1]];
      return { result: moveSelection(model, s.guids, delta, s.command === 'copy', newGuid), label: L(COMMAND_LABEL[s.command]), guides: [[base, to]], value: fmtLen(Math.hypot(delta[0], delta[1])) };
    }
    case 'rotate': {
      if (s.points.length === 0) return null;
      const pivot = s.points[0];
      const typedDeg = typed ? parseAngle(typed) : null;
      let angle: number | null = null;
      const guides: [XY, XY][] = [];
      if (s.points.length >= 2) {
        guides.push([pivot, s.points[1]]);
        if (at) {
          angle = ang(pivot, at.p) - ang(pivot, s.points[1]);
          guides.push([pivot, at.p]);
        }
      }
      if (typedDeg !== null) angle = (typedDeg * Math.PI) / 180;
      if (angle === null) return null;
      // Into (-180°, 180°].
      angle = Math.atan2(Math.sin(angle), Math.cos(angle));
      return { result: rotateSelection(model, s.guids, pivot, angle, s.copy, newGuid), label: L('Rotér'), guides, value: fmtDeg((angle * 180) / Math.PI) };
    }
    case 'mirror': {
      if (s.points.length !== 1 || !at) return null;
      return { result: mirrorSelection(model, s.guids, s.points[0], at.p, s.copy, newGuid), label: L('Spejl'), guides: [[s.points[0], at.p]], value: null };
    }
    case 'offset': {
      const w = model.walls?.find((x) => x.guid === s.guids[0]);
      const f = w ? frame(w) : null;
      if (!w || !f || !at) return null;
      const raw = dot(sub(at.p, w.start), f.normal);
      const offset = typedLen !== null ? Math.sign(raw || 1) * typedLen : raw;
      return { result: offsetWall(model, w.guid, offset, newGuid()), label: L('Forskyd'), guides: [], value: fmtLen(Math.abs(offset)) };
    }
    case 'split': {
      if (!at?.wall) return null;
      return { result: splitOps(model, at.wall, at.p, newGuid()), label: label(model, 'Del', [at.wall]), guides: [], value: null };
    }
    case 'join': {
      if (!at?.wall || at.wall === s.guids[0]) return null;
      return { result: joinOps(model, s.guids[0], at.wall), label: label(model, 'Saml hjørne', [s.guids[0], at.wall]), guides: [], value: null };
    }
    case 'trim': {
      if (!at?.wall || at.wall === s.guids[0]) return null;
      return { result: extendTo(model, s.guids[0], at.wall, at.p), label: label(model, 'Forlæng / trim', [at.wall]), guides: [], value: null };
    }
    case 'align': {
      if (!at?.line || at.line.guid === s.guids[0]) return null;
      return { result: alignWall(model, at.line, s.guids[0]), label: L('Juster'), guides: [[at.line.a, at.line.b]], value: null };
    }
    case 'merge':
      return null;
  }
}

/** A click: the session goes on (a point picked), commits the preview's batch (then goes on or ends), or stays. */
export type ClickOutcome = { session: ModifySession | null; commit: ModifyPreview | null };

/** The commands that stay on after a commit, for the next pick (Del, Forlæng / trim). */
const REPEATS: ReadonlySet<ModifyCommand> = new Set(['split', 'trim']);

/** Why a pick click did nothing — every click says something (Danish, for the toast). */
export function missedPick(s: ModifySession, at: PointerPick): string | null {
  switch (s.command) {
    case 'split':
      return 'Klik på den væg, der skal deles';
    case 'trim':
      return at.wall === s.guids[0] ? 'Det er grænsen — klik den væg, der skal forlænges eller trimmes' : 'Klik på den væg, der skal forlænges eller trimmes';
    case 'join':
      return at.wall === s.guids[0] ? 'Klik den anden væg' : 'Klik på den væg, hjørnet skal samles med';
    case 'align':
      return 'Klik på en væg eller et planspor at justere til';
    case 'offset':
      return 'Peg på den side, kopien skal ligge';
    default:
      return null;
  }
}

export function modifyClick(model: GeometriModel, s: ModifySession, at: PointerPick, typed: string | null, newGuid: () => string): ClickOutcome {
  const needsPoints: Partial<Record<ModifyCommand, number>> = { move: 1, copy: 1, rotate: 2, mirror: 1 };
  const need = needsPoints[s.command] ?? 0;
  if (s.points.length < need && !(s.command === 'rotate' && s.points.length === 1 && typed)) {
    return { session: { ...s, points: [...s.points, at.p] }, commit: null };
  }
  const preview = modifyPreview(model, s, at, typed, newGuid);
  if (!preview) {
    // Nothing under the click to work on: say so, and stay.
    const why = missedPick(s, at);
    return { session: s, commit: why ? { result: { error: why }, label: COMMAND_LABEL[s.command], guides: [], value: null } : null };
  }
  return { session: REPEATS.has(s.command) ? s : null, commit: preview };
}

/** Enter: commit what is typed (or, choosing the pivot, take the selection's centre). */
export function modifyEnter(model: GeometriModel, s: ModifySession, at: PointerPick | null, typed: string | null, newGuid: () => string): ClickOutcome {
  if (s.command === 'rotate' && s.points.length === 0) {
    const c = selectionCentre(model, s.guids);
    return { session: c ? { ...s, points: [c] } : s, commit: null };
  }
  if (!typed) return { session: s, commit: null };
  const preview = modifyPreview(model, s, at, typed, newGuid);
  return preview ? { session: REPEATS.has(s.command) ? s : null, commit: preview } : { session: s, commit: null };
}

/** Esc: one step back, or the end of the session (null). */
export function modifyBack(s: ModifySession): ModifySession | null {
  if (s.points.length === 0 || REPEATS.has(s.command)) return null;
  return { ...s, points: s.points.slice(0, -1) };
}
